import { FormField, Select } from "../../../../components/ui/FormComponents";
import { HouseholdComposition } from "../../../../types/mortgage";
import { HOUSEHOLD_SIZE_OPTIONS } from "../../../../constants";

interface HouseholdCompositionFormProps {
  household: HouseholdComposition;
  onChange: (household: HouseholdComposition) => void;
  householdExpenditure?: any;
  onsMatrix?: any;
}

export function HouseholdCompositionForm({ 
  household, 
  onChange, 
  householdExpenditure,
  onsMatrix 
}: HouseholdCompositionFormProps) {
  const updateHouseholdField = <K extends keyof HouseholdComposition>(
    field: K, 
    value: HouseholdComposition[K] | string
  ) => {
    // Convert string values to numbers, handling "3+" as 3
    let numericValue: number;
    if (typeof value === 'string') {
      if (value === '3+') {
        numericValue = 3;
      } else {
        numericValue = parseInt(value, 10);
      }
    } else {
      numericValue = value as number;
    }
    onChange({ ...household, [field]: numericValue as HouseholdComposition[K] });
  };

  // Helper function to get display value for select
  const getDisplayValue = (numericValue: number): string => {
    if (numericValue >= 3) {
      return '3+';
    }
    return numericValue.toString();
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
        <div className="flex flex-col">
          <FormField label="Number of adults" required>
            <Select
              value={getDisplayValue(household.adults)}
              onChange={(value) => updateHouseholdField('adults', value)}
              options={[...HOUSEHOLD_SIZE_OPTIONS.adults]}
            />
          </FormField>
        </div>
        
        <div className="flex flex-col">
          <FormField label="Number of children" required>
            <Select
              value={getDisplayValue(household.children)}
              onChange={(value) => updateHouseholdField('children', value)}
              options={[...HOUSEHOLD_SIZE_OPTIONS.children]}
            />
          </FormField>
        </div>
      </div>

      {/* ONS Expenditure Information */}
      {householdExpenditure && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md">
          <h4 className="font-subheader text-blue-900 dark:text-blue-200 mb-2">ONS Household Expenditure:</h4>
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p>Monthly expenditure: £{(householdExpenditure.totalMonthly / 100).toLocaleString()}</p>
            {onsMatrix && (
              <div className="mt-2 text-xs">
                <p>Based on ONS data: {household.adults} adult(s), {household.children} child(ren)</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
